import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import { Box, Container, Typography, Select, MenuItem, FormControl, Button, CircularProgress, Grid } from '@mui/material';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_URL = process.env.REACT_APP_API_URL || '';

const MACHINES = ['Machine1','Machine2','Machine3','Machine4','Machine5','Machine6','Machine7','Machine8'];

const theme = {
  bg:          '#04091a',
  cardBg:      '#071525',
  cardBg2:     '#050e1e',
  text:        '#B8F1FF',
  textMuted:   '#3a5a80',
  textSub:     '#8ab0cc',
  borderCard:  'rgba(40,120,255,0.28)',
  shadow:      '0 8px 32px rgba(0,0,0,0.85), 0 2px 8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(60,140,255,0.18)',
  shadowInset: 'inset 0 3px 12px rgba(0,0,0,0.75), inset 0 1px 4px rgba(0,0,0,0.6)',
};

const dateInputSx = {
  backgroundColor: theme.cardBg2,
  color: theme.text,
  border: `1px solid ${theme.borderCard}`,
  borderRadius: '6px',
  px: 1.5, py: 1.2,
  fontSize: '1rem',
  fontFamily: 'Roboto, sans-serif',
  outline: 'none',
  width: '100%',
  cursor: 'pointer',
  colorScheme: 'dark',
  '&:focus': { borderColor: '#4a90d9' },
  '&::-webkit-calendar-picker-indicator': { filter: 'invert(0.7) sepia(1) saturate(3) hue-rotate(180deg)', cursor: 'pointer' },
};

const selectSx = {
  color: theme.text,
  backgroundColor: theme.cardBg2,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.borderCard },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4a90d9' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4a90d9' },
  '& .MuiSelect-icon': { color: theme.textSub },
};

const AmperajesMaquina = () => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin]       = useState('');
  const [maquina, setMaquina]         = useState('Machine1');
  const [loading, setLoading]         = useState(false);
  const [chartData, setChartData]     = useState(null);
  const [error, setError]             = useState('');

  const handleConsultar = async () => {
    if (!fechaInicio || !fechaFin) { setError('Selecciona fecha inicial y fecha final'); return; }
    setLoading(true); setError(''); setChartData(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/promedio-amperaje-dia`, {
        params: { fechaInicio, fechaFin, maquinaId: maquina },
        headers: { Authorization: `Bearer ${token}` }
      });
      const datos = res.data.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
      if (!datos.length) { setError('Sin datos para el rango seleccionado.'); setLoading(false); return; }

      const labels = datos.map(d => new Date(d.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' }));
      setChartData({
        labels,
        datasets: [{ label: `Promedio Amperaje — ${maquina}`, data: datos.map(d => d.promedioAmperaje || 0), backgroundColor: 'rgba(74,144,217,0.7)', borderColor: '#4a90d9', borderWidth: 1, borderRadius: 4 }]
      });
    } catch { setError('Error al obtener los datos.'); }
    finally { setLoading(false); }
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: '#8ab0cc', font: { size: 11 } } },
      title: { display: true, text: 'Promedio de Amperaje Diario por Máquina', color: '#8ab0cc', font: { family: 'Roboto', size: 13 } },
      tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.y.toFixed(2)} A` } }
    },
    scales: {
      x: { grid: { color: 'rgba(30,90,180,0.2)', lineWidth: 0.5 }, ticks: { color: '#8ab0cc', font: { size: 10 } }, title: { display: true, text: 'Fecha', color: '#8ab0cc', font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: 'rgba(30,90,180,0.2)', lineWidth: 0.5 }, ticks: { color: '#8ab0cc', font: { size: 10 } }, title: { display: true, text: 'Amperaje (A)', color: '#8ab0cc', font: { size: 10 } } }
    }
  };

  return (
    <Box sx={{ backgroundColor: theme.bg, minHeight: '100vh', py: 3, px: { xs: 1, sm: 2 } }}>
      <Container maxWidth="xl">
        <Typography variant="h5" sx={{ color: theme.text, mb: 3, fontWeight: 600, letterSpacing: 1 }}>
          Amperajes por Máquina
        </Typography>

        <Box sx={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.borderCard}`, borderRadius: '10px', boxShadow: theme.shadow, p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: theme.textSub, fontSize: 12, pl: 0.5 }}>Fecha Inicio</Typography>
                <Box component="input" type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} sx={dateInputSx} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: theme.textSub, fontSize: 12, pl: 0.5 }}>Fecha Fin</Typography>
                <Box component="input" type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} sx={dateInputSx} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography sx={{ color: theme.textSub, fontSize: 12, pl: 0.5 }}>Máquina</Typography>
                <FormControl fullWidth>
                  <Select value={maquina} onChange={e => setMaquina(e.target.value)} sx={selectSx}
                    MenuProps={{ PaperProps: { sx: { backgroundColor: theme.cardBg, color: theme.text } } }}>
                    {MACHINES.map(m => <MenuItem key={m} value={m} sx={{ '&:hover': { backgroundColor: theme.cardBg2 } }}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button variant="contained" fullWidth onClick={handleConsultar} disabled={loading}
                sx={{ backgroundColor: '#0d4f8c', color: '#B8F1FF', fontWeight: 600, height: 50, '&:hover': { backgroundColor: '#1565c0' }, '&.Mui-disabled': { backgroundColor: theme.cardBg2, color: theme.textMuted } }}>
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Consultar'}
              </Button>
            </Grid>
          </Grid>
          {error && <Typography sx={{ color: '#ef5350', mt: 1.5, fontSize: 13 }}>{error}</Typography>}
        </Box>

        {chartData && (
          <Box sx={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.borderCard}`, borderRadius: '10px', boxShadow: theme.shadow, p: 2 }}>
            <Box sx={{ height: { xs: 320, sm: 460 }, backgroundColor: theme.cardBg2, borderRadius: '8px', border: `1px solid ${theme.borderCard}`, boxShadow: theme.shadowInset, p: 2 }}>
              <Bar data={chartData} options={options} />
            </Box>
          </Box>
        )}

        {!chartData && !loading && (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: theme.textMuted, fontSize: 15 }}>
              Selecciona fechas y máquina, luego pulsa Consultar
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default AmperajesMaquina;
